/**
 * Picks the MediaRecorder container and codecs for session recordings. Pure:
 * the browser's `MediaRecorder.isTypeSupported` is passed in.
 *
 * MP4 with H.264 + AAC comes first because it uploads to TikTok as-is. Chrome
 * and Edge 126+ and Safari record it natively. AAC is named explicitly
 * because a bare `video/mp4` gets Opus audio in Chrome. Other browsers fall
 * back to WebM, preferring H.264 inside it, so the file can be remuxed to MP4
 * without re-encoding. WebM is always saved with its real extension: a WebM
 * file renamed to .mp4 is rejected by many players and upload checks.
 */

const WITH_AUDIO = [
  'video/mp4;codecs=avc1.640028,mp4a.40.2', // H.264 High + AAC-LC (the browser raises the level as needed)
  'video/mp4;codecs=avc1,mp4a.40.2',
  'video/mp4;codecs=avc1,opus',
  'video/mp4',
  'video/webm;codecs=h264,opus',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

const VIDEO_ONLY = [
  'video/mp4;codecs=avc1.640028',
  'video/mp4;codecs=avc1',
  'video/mp4',
  'video/webm;codecs=h264',
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
];

const CONTAINERS = {
  'video/mp4': { extension: 'mp4', label: 'MP4' },
  'video/webm': { extension: 'webm', label: 'WebM' },
  'video/x-matroska': { extension: 'mkv', label: 'Matroska' },
};

/**
 * File details for a recorder MIME type. An empty type means the browser's
 * default container, which is WebM in every browser without MP4 support.
 * @returns {{container: string, extension: string, label: string, isMp4: boolean}}
 */
export function describeFormat(mimeType) {
  const container = String(mimeType ?? '').split(';')[0].trim().toLowerCase() || 'video/webm';
  const known = CONTAINERS[container] ?? CONTAINERS['video/webm'];
  return { container, ...known, isMp4: container === 'video/mp4' };
}

/**
 * A frame size hardware H.264 encoders handle for a `width`×`height` source.
 *
 * Some GPU encoders (seen in Brave/Chrome on Windows) read rows with the wrong
 * stride when the width isn't a multiple of 64, which shears every frame into
 * diagonal smears. So: same shape as the source, long edge at most `maxEdge`,
 * width a multiple of 64, height a multiple of 8 (1920×1080 stays 1920×1080).
 * @returns {{width: number, height: number}}
 */
export function encoderFrameSize(width, height, maxEdge = 1920) {
  if (!(width > 0 && height > 0)) return { width: 1920, height: 1080 };
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const w = Math.max(64, Math.floor((width * scale) / 64) * 64);
  const h = Math.max(8, Math.round((w * height) / width / 8) * 8);
  return { width: w, height: h };
}

/**
 * The best supported recording type, or `''` to let the browser choose.
 * @param {(type: string) => boolean} isTypeSupported
 * @param {{audio?: boolean}} options `audio`: the stream carries an audio track
 */
export function pickRecordingFormat(isTypeSupported, { audio = true } = {}) {
  const supported = (type) => {
    try {
      return Boolean(isTypeSupported(type));
    } catch {
      return false;
    }
  };
  const mimeType = (audio ? WITH_AUDIO : VIDEO_ONLY).find(supported) ?? '';
  return { mimeType, ...describeFormat(mimeType) };
}
